import React, { RefObject } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import { InvertedLoadingIndicator } from '@spotify-internal/anchor-ds';
import InfoButton from 'components/InfoButton';
import { trackEvent } from 'modules/analytics';
import { CarrotIcon } from 'shared/Icon/components/CarrotIcon';
import { paths } from 'client/shared/Paths';
import Search from 'components/svgs/Search';
import { MarketingButton } from 'components/MarketingPagesShared/MarketingButton';
import { stripHtml } from 'helpers/html';
import { TFunction, useT } from 'client/i18n/hooks/useT';
import {
  EpisodeCount,
  EpisodeCountText,
  PopupAnchor,
  InputWrapper,
  SearchIconWrapper,
  Message,
  SelectedPodcast,
  SelectedPodcastImage,
  SelectedPodcastInfo,
  SelectedPodcastName,
  DesktopEpisodeCount,
  MobileEpisodeCount,
  SelectedPodcastAuthor,
  SelectedPodcastDescription,
  ButtonWrapper,
  inputFieldClassName,
  typeaheadClassName,
  mutedTextColor,
  infoButtonClassName,
  caretIconClassName,
} from './styles';

export type TypeaheadOption = {
  podcastName: string;
  podcastDescription: string;
  authorName: string;
  episodeCount: number;
  image: string;
  feedUrl: string;
  type: string;
};

type Props = {
  isSearching: boolean;
  onSelectTypeahead: (options: TypeaheadOption[], t: TFunction) => void;
  onTypeaheadSearch: (value: string) => void;
  rssTypeaheadResults: TypeaheadOption[];
  renderTypeaheadOption: (option: TypeaheadOption) => React.ReactNode;
  rssFeedError: {
    message: string;
  };
  hasDataToContinue: boolean;
  fetchingRSSFeedImport: boolean;
  rssFeedIsValid: boolean;
  onClickGetStarted: () => void;
  rssFeedMetadata: {
    authorName: string;
    authorEmail: string;
    image: string;
    isExplicit: boolean;
    itunesCategory: string;
    language: string;
    podcastName: string;
    podcastDescription: string;
    episodeCount: number;
  };
  autoFocus?: boolean;
  inputRef?: RefObject<AsyncTypeahead<TypeaheadOption>>;
};

export const ImportForm = ({
  isSearching,
  onSelectTypeahead,
  onTypeaheadSearch,
  rssTypeaheadResults,
  renderTypeaheadOption,
  rssFeedError,
  hasDataToContinue,
  fetchingRSSFeedImport,
  rssFeedIsValid,
  onClickGetStarted,
  rssFeedMetadata,
  autoFocus,
  inputRef,
}: Props) => {
  const t = useT();
  const renderEpisodeCount = () => (
    <div>
      <EpisodeCount>
        <EpisodeCountText>
          {t(
            'ImportScreen.ImportSection.ImportForm.EpisodesCountText',
            '{episodeCount, plural, one {1 Episode} other {{episodeCount} Episodes}}',
            'Episodes import count text',
            { episodeCount: rssFeedMetadata.episodeCount }
          )}
        </EpisodeCountText>
        <InfoButton
          direction="top"
          width={230}
          activeOnHover={false}
          infoBubbleClassName={infoButtonClassName}
        >
          {t(
            'ImportScreen.ImportSection.ImportForm.EpisodesDisclaimer',
            'This episode count might not reflect all of your episodes. {learnMoreLink}',
            'Episodes import disclaimer',
            {
              learnMoreLink: (
                <PopupAnchor
                  href={paths.getHelpPage('troubleshooting')}
                  target="_blank"
                  rel="noopener noreferrer"
                  // Otherwise the popover closes before it navigates to the URL
                  onMouseDown={e => e.preventDefault()}
                  key="import-screen__form-learn-more-link"
                >
                  {t('Link.LearnMore', 'Learn more', 'Learn more link text')}
                  <CarrotIcon
                    fillColor="white"
                    size={{ width: 7, height: 8 }}
                    className={caretIconClassName}
                  />
                </PopupAnchor>
              ),
            }
          )}
        </InfoButton>
      </EpisodeCount>
    </div>
  );

  const episodeCount = renderEpisodeCount();

  return (
    <div>
      <FormGroup id="podcastName">
        <InputWrapper>
          <AsyncTypeahead
            caseSensitive={true}
            autoFocus={autoFocus}
            emptyLabel={t(
              'ImportScreen.ImportSection.ImportForm.TypeaheadEmptyLabel',
              'No podcasts or RSS feeds found.',
              'Podcast search field label when empty'
            )}
            promptText={t(
              'ImportScreen.ImportSection.ImportForm.TypeaheadPromptText',
              'Type to search...',
              'Podcast search field prompt text'
            )}
            searchText={t(
              'ImportScreen.ImportSection.ImportForm.TypeaheadSearchingText',
              'Searching...',
              'Podcast search field searching text'
            )}
            isLoading={isSearching}
            inputProps={{
              className: inputFieldClassName,
              'aria-label': t(
                'ImportScreen.ImportSection.ImportForm.TypeaheadAriaLabel',
                'Enter your podcast name or RSS feed',
                'Podcast search field screenreader information'
              ),
            }}
            // @ts-ignore (for some reason this property isn't in the types)
            className={typeaheadClassName}
            labelKey={option => option.podcastName || ''}
            filterBy={() => true}
            onChange={option => onSelectTypeahead(option, t)}
            onSearch={onTypeaheadSearch}
            options={isSearching ? [] : rssTypeaheadResults}
            ref={inputRef}
            placeholder={t(
              'ImportScreen.ImportSection.ImportForm.TypeaheadPlaceholder',
              'Enter your podcast name or RSS feed',
              'Podcast search field placeholder'
            )}
            renderMenuItemChildren={renderTypeaheadOption}
          />
          <SearchIconWrapper>
            <Search size={24} color={mutedTextColor} />
          </SearchIconWrapper>
        </InputWrapper>
        {rssFeedError && rssFeedError.message && (
          <Message>{rssFeedError.message}</Message>
        )}
        {hasDataToContinue && !rssFeedIsValid && rssFeedError.message && (
          <Message>
            {t(
              'ImportScreen.ImportSection.ImportForm.FeedInvalidErrorMessage',
              'Sorry, the feed is not valid',
              'Search by RSS feed error message when feed invalid'
            )}
          </Message>
        )}
      </FormGroup>

      {hasDataToContinue && (
        <SelectedPodcast>
          <SelectedPodcastImage
            src={rssFeedMetadata.image}
            alt={t(
              'ImportScreen.ImportSection.ImportForm.PodcastImageAlt',
              'Cover for the podcast {podcastName}',
              'Podcast cover image screenreader information',
              { podcastName: rssFeedMetadata.podcastName }
            )}
          />
          <SelectedPodcastInfo>
            <SelectedPodcastName>
              {rssFeedMetadata.podcastName}
              <DesktopEpisodeCount>{episodeCount}</DesktopEpisodeCount>
            </SelectedPodcastName>
            {rssFeedMetadata.authorName && (
              <SelectedPodcastAuthor>
                by {rssFeedMetadata.authorName}
              </SelectedPodcastAuthor>
            )}
            {rssFeedMetadata.podcastDescription && (
              <SelectedPodcastDescription>
                {stripHtml(rssFeedMetadata.podcastDescription)}
              </SelectedPodcastDescription>
            )}
            <ButtonWrapper>
              <MarketingButton
                kind="button"
                onClick={() => {
                  onClickGetStarted();
                  trackEvent(
                    'importing_ux_get_started_button_clicked',
                    {},
                    // @ts-ignore
                    { providers: [mParticle] }
                  );
                }}
              >
                {t(
                  'ImportScreen.ImportSection.ImportForm.SwitchPodcastButtonLabel',
                  'Switch your podcast',
                  'Switch your podcast to Anchor button text'
                )}
              </MarketingButton>
            </ButtonWrapper>
            <MobileEpisodeCount>{episodeCount}</MobileEpisodeCount>
          </SelectedPodcastInfo>
        </SelectedPodcast>
      )}
      {fetchingRSSFeedImport && <InvertedLoadingIndicator />}
    </div>
  );
};
